/**
 * useFormatPrice Hook
 *
 * Hook for formatting prices according to the user's selected currency.
 * Uses the currency store for consistent formatting across the app.
 */

import { useCurrencyStore } from '@/stores/useCurrencyStore';

/**
 * Returns a function to format prices in the user's selected currency.
 *
 * @example
 * const formatPrice = useFormatPrice();
 * const formattedPrice = formatPrice(29.99); // "€29.99" or "£29.99"
 */
export const useFormatPrice = () => {
    const currency = useCurrencyStore((state) => state.currency);
    const formatPrice = useCurrencyStore((state) => state.formatPrice);

    return (amount: number) => {
        void currency;
        return formatPrice(amount);
    };
};

/**
 * Returns both the format function and current currency info.
 *
 * @example
 * const { formatPrice, currencyInfo, currency } = useCurrency();
 */
export const useCurrency = () => {
    const currency = useCurrencyStore((state) => state.currency);
    const setCurrency = useCurrencyStore((state) => state.setCurrency);
    const formatPrice = useCurrencyStore((state) => state.formatPrice);
    const getCurrencyInfo = useCurrencyStore((state) => state.getCurrencyInfo);

    return {
        currency,
        setCurrency,
        formatPrice,
        currencyInfo: getCurrencyInfo(),
    };
};

export default useFormatPrice;
